// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Button, { ButtonPresentationProps } from "@awc/MobiusOverrides/Button/Button";
import DataTable from "@awc/MobiusOverrides/DataTable/DataTable";
import DataTableBody from "@awc/MobiusOverrides/DataTable/DataTableBody";
import DataTableCell from "@awc/MobiusOverrides/DataTable/DataTableCell";
import DataTableHead from "@awc/MobiusOverrides/DataTable/DataTableHead";
import DataTableHeader from "@awc/MobiusOverrides/DataTable/DataTableHeader";
import DataTableRow from "@awc/MobiusOverrides/DataTable/DataTableRow";
import Select, { SelectPresentationProps } from "@awc/MobiusOverrides/Select";
import TextField, { TextFieldPresentationProps } from "@awc/MobiusOverrides/TextField";
import { XNGAGE_ELEMENTS } from "@awc/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTablePresentationProps } from "@insite/mobius/DataTable";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";

type Props = WidgetProps & HasProduct;

export interface VoltageDropCalculatorStyles {
    table?: DataTablePresentationProps;
    materialSelect?: SelectPresentationProps;
    sizeSelect?: SelectPresentationProps;
    phaseSelect?: SelectPresentationProps;
    cableLengthTextField?: TextFieldPresentationProps;
    loadTextField?: TextFieldPresentationProps;
    estimatedVoltageTextField?: TextFieldPresentationProps;
    voltageDropPercentageTextField?: TextFieldPresentationProps;
    conductorTextField?: TextFieldPresentationProps;
    sectionTitle?: TypographyPresentationProps;
    textFieldAndUnitWrapper?: InjectableCss;
    calculateButton?: ButtonPresentationProps;
}

export const voltageDropCalculatorStyles: VoltageDropCalculatorStyles = {};

const styles = voltageDropCalculatorStyles;

interface VdOption {
    value: number;
    display: string;
}

interface VdPhaseOption extends VdOption {
    phase: number;
    voltage: number;
}

interface VoltageDropResult {
    voltDrop?: number;
    voltDropPercentage?: number;
    sizeSelected?: number;
}

const vdMaterials: VdOption[] = [
    { value: 12.9, display: translate("Copper") },
    { value: 21.2, display: translate("Aluminum") },
];

const vdSizes: VdOption[] = [
    { value: 1620, display: translate("18 AWG") },
    { value: 2580, display: translate("16 AWG") },
    { value: 4110, display: translate("14 AWG") },
    { value: 6530, display: translate("12 AWG") },
    { value: 10380, display: translate("10 AWG") },
    { value: 16510, display: translate("8 AWG") },
    { value: 26240, display: translate("6 AWG") },
    { value: 41740, display: translate("4 AWG") },
    { value: 52620, display: translate("3 AWG") },
    { value: 66360, display: translate("2 AWG") },
    { value: 83690, display: translate("1 AWG") },
    { value: 105600, display: translate("1/0 AWG") },
    { value: 133100, display: translate("2/0 AWG") },
    { value: 167800, display: translate("3/0 AWG") },
    { value: 211600, display: translate("4/0 AWG") },
    { value: 250000, display: translate("250 kcmil") },
    { value: 300000, display: translate("300 kcmil") },
    { value: 350000, display: translate("350 kcmil") },
    { value: 400000, display: translate("400 kcmil") },
    { value: 500000, display: translate("500 kcmil") },
    { value: 600000, display: translate("600 kcmil") },
    { value: 700000, display: translate("700 kcmil") },
    { value: 750000, display: translate("750 kcmil") },
    { value: 800000, display: translate("800 kcmil") },
    { value: 900000, display: translate("900 kcmil") },
    { value: 100000, display: translate("1000 kcmil") },
    { value: 1250000, display: translate("1250 kcmil") },
    { value: 1500000, display: translate("1500 kcmil") },
    { value: 1750000, display: translate("1750 kcmil") },
    { value: 2000000, display: translate("2000 kcmil") },
];

const vdPhases: VdPhaseOption[] = [
    { value: 2, display: translate("120 Volts 1-phase"), phase: 1, voltage: 120 },
    { value: 2, display: translate("240 Volts 1-phase"), phase: 1, voltage: 240 },
    { value: 1.732, display: translate("208 Volts 3-phase 3-wire"), phase: 3, voltage: 208 },
    { value: 1.732, display: translate("120/208 Volts 3-phase 4-wire"), phase: 3, voltage: 120 },
    { value: 2, display: translate("277 Volts 1-phase"), phase: 1, voltage: 277 },
    { value: 1.732, display: translate("480 Volts 3-phase"), phase: 3, voltage: 480 },
    { value: 1.732, display: translate("277/480 3-phase 4-wire"), phase: 3, voltage: 277 },
    { value: 2, display: translate("24 DC or 1-phase AC"), phase: 1, voltage: 120 },
    { value: 2, display: translate("48 DC or 1-phase AC"), phase: 1, voltage: 120 },
];

const VoltageDropCalculator: React.FC<Props> = () => {
    const [material, setMaterial] = React.useState<number>(vdMaterials[0].value);
    const [size, setSize] = React.useState<number>(vdSizes[0].value);
    const [phase, setPhase] = React.useState<string>(vdPhases[0].display);
    const [length, setLength] = React.useState<number | undefined>(undefined);
    const [load, setLoad] = React.useState<number | undefined>(undefined);
    const [result, setResult] = React.useState<VoltageDropResult | undefined>(undefined);

    const handleSubmit = (event: any) => {
        event.preventDefault();
        const phaseItem = vdPhases?.find(phaseItem => phaseItem.display === phase);
        let voltDrop;
        let trimmedVoltDrop;
        let voltDropPercentage;
        if (phaseItem && load != null && length != null) {
            voltDrop = (phaseItem.value * material * load * length) / size;
            trimmedVoltDrop = Math.round(voltDrop * 10) / 10;
            voltDropPercentage = (trimmedVoltDrop / phaseItem.voltage) * 100;
        }
        setResult({
            voltDrop: trimmedVoltDrop,
            voltDropPercentage: voltDropPercentage ? Math.round(voltDropPercentage * 10) / 10 : undefined,
            sizeSelected: size,
        });
    };

    return (
        <DataTable {...styles.table} data-test-selector="voltageDropCalculator">
            <DataTableHead>
                <DataTableHeader colSpan={2}>
                    <Typography {...styles.sectionTitle}>{translate("Required Data Entry")}</Typography>
                </DataTableHeader>
            </DataTableHead>
            <DataTableBody aria-label={translate("Data Entry")}>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="materialSelect">{translate("Select Material")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <Select
                            id="materialSelect"
                            value={material || ""}
                            required
                            onChange={(event: React.FormEvent<HTMLSelectElement>) => {
                                setMaterial(Number(event.currentTarget.value));
                            }}
                            {...styles.materialSelect}
                        >
                            {vdMaterials.map(material => (
                                <option key={material.value} value={material.value}>
                                    {material.display}
                                </option>
                            ))}
                        </Select>
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="sizeSelect">{translate("Select Size")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <Select
                            id="sizeSelect"
                            value={size || ""}
                            required
                            onChange={(event: React.FormEvent<HTMLSelectElement>) => {
                                setSize(Number(event.currentTarget.value));
                            }}
                            {...styles.sizeSelect}
                        >
                            {vdSizes.map(size => (
                                <option key={size.value} value={size.value}>
                                    {size.display}
                                </option>
                            ))}
                        </Select>
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="phaseSelect">{translate("Select Voltage and Phase")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <Select
                            id="phaseSelect"
                            value={phase || ""}
                            required
                            onChange={(event: React.FormEvent<HTMLSelectElement>) => {
                                setPhase(event.currentTarget.value);
                            }}
                            {...styles.phaseSelect}
                        >
                            {vdPhases.map(phase => (
                                <option key={phase.display} value={phase.display}>
                                    {phase.display}
                                </option>
                            ))}
                        </Select>
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="cableLength">{translate("Enter Cable Length")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <StyledWrapper {...styles.textFieldAndUnitWrapper}>
                            <TextField
                                id="cableLength"
                                value={length || ""}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setLength(Number(event.target.value));
                                }}
                                validateAs="number"
                                required={true}
                                showError={false}
                                {...styles.cableLengthTextField}
                                // XNG-Changes
                                maxLength={16}
                            />
                            <Typography>{translate("Feet")}</Typography>
                        </StyledWrapper>
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="load">{translate("Enter Load")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <StyledWrapper {...styles.textFieldAndUnitWrapper}>
                            <TextField
                                id="load"
                                value={load || ""}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setLoad(Number(event.target.value));
                                }}
                                validateAs="number"
                                showError={false}
                                required={true}
                                {...styles.loadTextField}
                                // XNG-Changes
                                maxLength={16}
                            />
                            <Typography>{translate("Amps")}</Typography>
                        </StyledWrapper>
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell colSpan={2}>
                        <Button onClick={handleSubmit} type="submit" {...styles.calculateButton}>
                            {translate("Calculate")}
                        </Button>
                    </DataTableCell>
                </DataTableRow>
            </DataTableBody>

            <DataTableHead>
                <DataTableHeader colSpan={2}>
                    <Typography {...styles.sectionTitle}>{translate("Results")}</Typography>
                </DataTableHeader>
            </DataTableHead>
            <DataTableBody>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="estimatedVoltDrop">{translate("Estimated Volt Drop")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <TextField
                            id="estimatedVoltDrop"
                            value={result?.voltDrop || ""}
                            onChange={() => {}}
                            {...styles.estimatedVoltageTextField}
                        />
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="voltDropPercentage">{translate("Volt Drop %")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <TextField
                            id="voltDropPercentage"
                            value={result?.voltDropPercentage || ""}
                            onChange={() => {}}
                            {...styles.voltageDropPercentageTextField}
                        />
                    </DataTableCell>
                </DataTableRow>
                <DataTableRow>
                    <DataTableCell>
                        <label htmlFor="conductor">{translate("Conductor")}</label>
                    </DataTableCell>
                    <DataTableCell>
                        <TextField
                            id="conductor"
                            value={result?.sizeSelected || ""}
                            onChange={() => {}}
                            {...styles.conductorTextField}
                        />
                    </DataTableCell>
                </DataTableRow>
            </DataTableBody>
        </DataTable>
    );
};

const widgetModule: WidgetModule = {
    component: VoltageDropCalculator,
    definition: {
        group: XNGAGE_ELEMENTS,
    },
};

export default widgetModule;
